import type { Map as MapLibreMap } from "maplibre-gl";

import type { CheckpointPoint } from "../data/map-points";
import { closeRing, type Ring } from "../data/site-boundary";
import type { SiteAsset } from "../data/site-asset";
import { circlePolygonRing } from "./geofence-circle";
import { MAP_THEME } from "./map-theme";
import { appendPolygon, appendPolyline } from "./map-svg-overlay-primitives";

export interface PropertyMapSvgOverlayModel {
  boundaryRing: Ring | null;
  assetZones: readonly Pick<SiteAsset, "name" | "ring">[];
  draftRing: Ring;
  draftActive: boolean;
  checkpoints: readonly CheckpointPoint[];
}

/** SVG overlay for property boundary + geofence preview (MapTiler hybrid hides GeoJSON fills). */
export function renderPropertyMapSvgOverlay(
  map: MapLibreMap,
  svg: SVGSVGElement,
  model: PropertyMapSvgOverlayModel,
): void {
  const width = map.getCanvas().clientWidth;
  const height = map.getCanvas().clientHeight;
  svg.setAttribute("width", String(width));
  svg.setAttribute("height", String(height));
  svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
  svg.replaceChildren();

  if (model.draftActive) {
    appendPolyline(svg, model.draftRing, map, "#5b8fff", 4, true);
    if (model.draftRing.length >= 3) {
      appendPolygon(svg, model.draftRing, map, "rgba(91, 143, 255, 0.32)", "#5b8fff", 3);
    }
    return;
  }

  if (model.boundaryRing !== null && model.boundaryRing.length >= 3) {
    appendPolygon(
      svg,
      closeRing(model.boundaryRing),
      map,
      "rgba(91, 143, 255, 0.45)",
      "#5b8fff",
      4,
    );
  }

  for (const asset of model.assetZones) {
    if (asset.ring.length < 3) {
      continue;
    }
    appendPolygon(
      svg,
      closeRing(asset.ring),
      map,
      MAP_THEME.siteAssetFill,
      MAP_THEME.siteAssetStroke,
      2,
    );
  }

  for (const checkpoint of model.checkpoints) {
    if (checkpoint.geofenceRadiusMeters === null || checkpoint.geofenceRadiusMeters <= 0) {
      continue;
    }
    const ring = circlePolygonRing(checkpoint.lng, checkpoint.lat, checkpoint.geofenceRadiusMeters);
    if (ring.length < 4) {
      continue;
    }
    appendPolygon(
      svg,
      ring,
      map,
      MAP_THEME.geofenceFill,
      MAP_THEME.geofenceStroke,
      2,
    );
  }
}
